import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import PersonIcon from '@mui/icons-material/Person';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import type { PlaywrightTest, PlaywrightTestGroup } from '../PlaywrightTests';

interface PlaywrightTestTreeProps {
  testGroups: PlaywrightTestGroup[];
  onSelectTest: (test: PlaywrightTest) => void;
}

const StatusIcon: React.FC<{ status: PlaywrightTest['status'] }> = ({ status }) => {
  const iconStyle = { fontSize: 18, mr: 1 };

  switch (status) {
    case 'passed':
      return <CheckCircleIcon sx={{ ...iconStyle, color: 'success.main' }} />;
    case 'failed':
      return <CancelIcon sx={{ ...iconStyle, color: 'error.main' }} />;
    case 'skipped':
      return <RemoveCircleOutlineIcon sx={{ ...iconStyle, color: 'warning.main' }} />;
    case 'not-run':
    default:
      return <CircleOutlinedIcon sx={{ ...iconStyle, color: 'grey.500' }} />;
  }
};

const getStatusColor = (status: PlaywrightTest['status']): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'passed':
      return 'success';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'warning';
    default:
      return 'default';
  }
};

export const PlaywrightTestTree: React.FC<PlaywrightTestTreeProps> = ({
  testGroups,
  onSelectTest,
}) => {
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());

  const handleRoleToggle = (role: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(role)) {
      newExpanded.delete(role);
    } else {
      newExpanded.add(role);
    }
    setExpandedRoles(newExpanded);
  };

  const handleTagToggle = (roleTag: string) => {
    const newExpanded = new Set(expandedTags);
    if (newExpanded.has(roleTag)) {
      newExpanded.delete(roleTag);
    } else {
      newExpanded.add(roleTag);
    }
    setExpandedTags(newExpanded);
  };

  const getTestCount = (group: PlaywrightTestGroup) => {
    return Object.values(group.tags).reduce((sum, tests) => sum + tests.length, 0);
  };

  const getStatusCounts = (tests: PlaywrightTest[]) => {
    const counts = { passed: 0, failed: 0, skipped: 0, notRun: 0 };
    tests.forEach(test => {
      if (test.status === 'passed') counts.passed++;
      else if (test.status === 'failed') counts.failed++;
      else if (test.status === 'skipped') counts.skipped++;
      else counts.notRun++;
    });
    return counts;
  };

  if (testGroups.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="text.secondary">
          No test results available. Run the tests to see results here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {testGroups.map(group => {
        const testCount = getTestCount(group);
        if (testCount === 0) return null;

        return (
          <Accordion
            key={group.role}
            expanded={expandedRoles.has(group.role)}
            onChange={() => handleRoleToggle(group.role)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold">
                  {group.role}
                </Typography>
                <Chip label={`${testCount} tests`} size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {Object.entries(group.tags).map(([tag, tests]) => {
                const roleTagKey = `${group.role}:${tag}`;
                const counts = getStatusCounts(tests);

                return (
                  <Accordion
                    key={roleTagKey}
                    expanded={expandedTags.has(roleTagKey)}
                    onChange={() => handleTagToggle(roleTagKey)}
                    sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ pl: 4, bgcolor: 'action.hover' }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalOfferIcon fontSize="small" />
                        <Typography variant="body1">@{tag}</Typography>
                        <Chip label={`${tests.length}`} size="small" variant="outlined" />
                        {counts.passed > 0 && (
                          <Chip
                            label={`✓ ${counts.passed}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {counts.failed > 0 && (
                          <Chip
                            label={`✗ ${counts.failed}`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <List sx={{ pl: 2 }}>
                        {tests.map(test => (
                          <ListItem key={test.id} disablePadding>
                            <ListItemButton onClick={() => onSelectTest(test)}>
                              <StatusIcon status={test.status} />
                              <ListItemText
                                primary={test.name}
                                secondary={test.file}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: test.status === 'failed' ? 'bold' : 'normal',
                                }}
                                secondaryTypographyProps={{
                                  variant: 'caption',
                                }}
                              />
                              {test.duration !== undefined && (
                                <Chip
                                  label={`${Math.round(test.duration)}ms`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                              )}
                              <Chip
                                label={test.status}
                                size="small"
                                color={getStatusColor(test.status)}
                                sx={{ ml: 1 }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
